<template>
	<!-- where the tables rest -->
	<div
	class="table-bed prevent-auto-margins"
	:class="{
		'standalone': standAlone,
		'has-more': hasMore,
		'mobile-style': getMobileAppState,
		'no-header': hideHeader,
		'force-header': applyHeaderForMobile,
		'no-box-styles': noBoxStyles
	}">
		<table class="data-table-wrapper">
			<!-- List header -->
			<component
			v-if="(!getMobileAppState || applyHeaderForMobile) && !hideHeader && !alwaysBehaveLikeMobile"
			:is="headerComponent"
			:headerData="headerParsed"
			:tableStyles="fixedTableStyles"
			:getMobileAppState="getMobileAppState"
			@sortBy="$emit('sortBy', $event)"/>
			<!-- List body with rows -->
			<table-body-base
            :tableIdentifier="tableIdentifier"
			:rowType="rowType"
			:rowHeightClass="rowHeightClass"
			:tableData="tableDataParsed"
			:tableStyles="fixedTableStyles"
			:configurationData="headerParsed"
			:disabled="disabled"
			:hasDetail="hasDetail"
			:alwaysBehaveLikeMobile="alwaysBehaveLikeMobile"
			@rowClicked="$emit('rowClicked', $event)"
			@closeRowClicked="emit('closeRowClicked', $event)"
			@moreOptionsClicked="$emit('moreOptionsClicked', $event)"
			@selectedRadio="emit('selectedRadio', $event)"
			@toggleSwitch="emit('toggleSwitch', $event)"
			@dropdownClicked="emit('dropdownClicked', $event)"
			@onBlur="emit('onBlur', $event)"
			@inputChanged="emit('inputChanged', $event)"
			@downloadClicked="emit('downloadClicked', $event)"
			@buttonClicked="$event.dataAttr === 'rowDetailButton' ? $emit('rowClicked', $event) : emit('buttonClicked', $event)"/>
		</table>
		<!-- Load More -->
		<load-more
        v-if="hasMore"
        :isLoadingMore="isLoadingMore"
        @loadMore="emit('loadMore')" />
	</div>
</template>

<script setup>
import TableBodyBase from '@/components/generic/table/BodyBase'
import LoadMore from '@/components/generic/table/LoadMore'
/* Vue */
import { computed, defineAsyncComponent } from 'vue'

/* Store */
import { mapGetters } from '@/store/helpers/mappers'

/* Emmitters */
const emit = defineEmits(['rowClicked', 'closeRowClicked', 'moreOptionsClicked', 'selectedRadio', 'toggleSwitch', 'loadMore', 'dropdownClicked', 'inputChanged', 'sortBy', 'onBlur', 'downloadClicked', 'buttonClicked'])

/** Props received:
@tableIdentifier {String} - prop to help for dropdown teleports when we have multiple tables in same feature
@standAlone { Boolean } - prop to apply standAlone class, making our table have its own "box"
@disabled { Boolean } - prop to disabled clicks on table
@hasDetail { Boolean } - default value is true, prop to add hover styles and icon at the right that shows that row has detail
@detailCustomIcon {String} - custom icon for detail button
@noBoxStyles { Boolean } - prop to remove box styles
@hideDetailButton {Boolean} - prop to hide detail button, but still alows click on row
@rowType { String } - prop to define row type [generic(default)]
@rowHeightClass { String } - prop to apply class to row, to control it's height: [default height(76px), medium(96px)]
@applyHeaderForMobile {Boolean} - prop to force header to appear for mobile table, since most common behaviour is to have it hidden
@headerType { String } - prop to define header type [generic(default), noCapsDarkBlue]
@headerData { Array } in which each Object is a column in your list with:
	- label [String]: text shown under the step
	- sortable [Boolean]: there are some tables (without pagination) that require a sortable option
	- dataAttr [String, Number]: identifier, used for sortable and to create groups, use (when available) prop provided by MDW for this column
	- showInMobile [Boolean, Object]:
		-[Boolean] if we receive a boolean your cell in mobile will be a column
		-[Object] if we receive an object, your cell will have multiple lines in one column
			key: groupWith, value: dataAttr we want to pair with this cell in mobile layout
@hideHeader {Boolean}
@tableData {Array} -
@tableStyles {Array} -
@hasMore {Boolean}
@loadMoreText {String} - Text for loadMore button
@loadingMoreText {String} - Text to display while loading more
@isLoadingMore {Boolean} - prop to tell table when to show loader or button
@alwaysBehaveLikeMobile {Boolean} - prop to force mobile layout even in higher resolutions
*/
const props = defineProps({
    tableIdentifier: String,
	standAlone: { type: Boolean, default: () => true },
	noBoxStyles: Boolean,
	disabled: Boolean,
	hasDetail: { type: Boolean, default: () => true },
	detailCustomIcon: String,
	hideDetailButton: Boolean,
	rowType: { type: String, default: () => 'generic' },
	rowHeightClass: String,
	applyHeaderForMobile: Boolean,
	headerType: { type: String, default: () => 'generic' },
	headerData: Array,
	hideHeader: Boolean,
	tableData: Array,
	tableStyles: Array,
	hasMore: Boolean,
	loadMoreText: String,
	loadingMoreText: String,
	isLoadingMore: Boolean,
	alwaysBehaveLikeMobile: Boolean
})

const { getMobileAppState } = mapGetters()

/* Dynamic Header Component */
const headerComponent = computed(() => {
	return defineAsyncComponent(() => import('@/components/generic/table/headers/' + headerComponentMapping[props.headerType]))
})

const headerParsed = computed(() => {
	if (!props.hasDetail || props.hideDetailButton) return props.headerData
	return [...props.headerData, { type: 'button', showInMobile: true, dataAttr: 'rowDetailButton', rowCellType: 'button' }]
})

const stylesParsed = computed(() => {
	if (!props.hasDetail || props.hideDetailButton) return props.tableStyles
	return [...props.tableStyles, null]
})

const tableDataParsed = computed(() => {
	if (!props.hasDetail || props.hideDetailButton) return props.tableData
	return props.tableData.map(row => {
		return [...row, { type: 'button', value: props.detailCustomIcon || 'arrow-right-full', dataAttr: 'rowDetailButton' }]
	})
})

/*
* Here we are adding to our tableStyles, width/max-width for specific cells type
*/
const fixedTableStyles = computed(() => {
	const styles = stylesParsed.value || headerParsed.value.map(elem => null)

	return styles.map((column, index) => {
		const temp = { ...column }

		if (headerParsed.value[index].rowCellType) {
			const isFirstCell = index === 0
			const isLastCell = index === (styles.length - 1)
			const paddingForCell = 16
			switch (headerParsed.value[index].rowCellType) {
				case 'category':
					temp.width = temp.width || `${32 + paddingForCell + (isFirstCell ? 16 : 0)}px`
					temp['min-width'] = temp['min-width'] || `${32 + paddingForCell + (isFirstCell ? 16 : 0)}px`
					temp.padding = temp.padding || 0
					temp['padding-left'] = temp['padding-left'] || '16px'
					break
				case 'button':
				case 'download':
				case 'more-options':
					/* If we are in iOS we will want hide more-options */
					temp.width = temp.width || (getMobileAppState.value && !isLastCell ? '40px' : `${40 + paddingForCell}px`)
					temp['min-width'] = temp['min-width'] || (getMobileAppState.value && !isLastCell ? '40px' : `${40 + paddingForCell}px`)
					temp.padding = temp.padding || (isLastCell ? '0 8px 0 0' : 0)
					break
				case 'close':
					temp.width = temp.width || `${24 + paddingForCell}px`
					temp['min-width'] = temp['min-width'] || `${24 + paddingForCell}px`
					temp.padding = temp.padding || 0
					break
                case 'icon':
					temp.width = temp.width || `${24 + paddingForCell}px`
					temp['min-width'] = temp['min-width'] || `${24 + paddingForCell}px`
					temp.padding = temp.padding || (isLastCell ? '0 16px 0 0' : 0)
					break
				case 'radio':
					temp.width = temp.width || `${42 + paddingForCell}px`
					temp['min-width'] = temp['min-width'] || `${42 + paddingForCell}px`
					temp['text-align'] = temp['text-align'] || 'end'
					break
				case 'switch':
					temp.width = temp.width || `${66 + paddingForCell}px`
					temp['min-width'] = temp['min-width'] || `${66 + paddingForCell}px`
					break
				case 'status':
					if (!getMobileAppState.value) break

					temp.width = temp.width || `${16 + paddingForCell}px`
					temp['min-width'] = temp['min-width'] || `${16 + paddingForCell}px`
					break
				case 'acronym':
					temp.width = temp.width || `${32 + 8 + (isFirstCell ? 16 : 0)}px`
					temp['min-width'] = temp['min-width'] || `${32 + 8 + (isFirstCell ? 16 : 0)}px`
					temp.padding = temp.padding || (isFirstCell ? '0 0 0 16px' : 0)
					break
				case 'agency-pin':
					if (!getMobileAppState.value) break
					temp.width = temp.width || `${32 + 8 + (isFirstCell ? 16 : 0)}px`
					temp['min-width'] = temp['min-width'] || `${32 + 8 + (isFirstCell ? 16 : 0)}px`
					break
			}
		}
		return temp
	})
})

/** maps the list of components that are used as cell */
const headerComponentMapping = {
	generic: 'Generic'
}
</script>

<style lang="scss" scoped>
.table-bed {
    width: 100%;
	flex-shrink: 0;

	&.standalone {
		&.mobile-style:not(.force-header) {
			padding: 0;

			.load-more-wrapper {
				border: unset;
			}
		}

		@media only screen and (max-width: 1024px) {
			padding: 16px 0 0 0;

			&.mobile-style:not(.force-header) {
				padding: 0;
			}
		}
	}

	&.no-header {
		padding: 0;

		.data-table-wrapper {
			width: 100%;

			.data-table-body-wrapper {
				:deep(.data-table-row) {
					&:first-of-type {
						td { border-top: 1px solid var(--secondary-light-2); }
						td:first-of-type { border-top-left-radius: 12px; }
						td:last-of-type { border-top-right-radius: 12px; }
					}
				}
			}
		}

		&.mobile-style {
			.data-table-wrapper {
				width: 100%;

				.data-table-body-wrapper {
					:deep(.data-table-row) {
						&:first-of-type {
							td {
								border-top: unset;
							}
						}

						td {
							border-left: unset;
							border-right: unset;
						}
					}
				}
			}
		}
	}

	&.has-more {
		.data-table-wrapper {
			width: 100%;

			:deep(.data-table-row) {
				&:last-of-type {
					td:first-of-type { border-bottom-left-radius: 0; }
					td:last-of-type { border-bottom-right-radius: 0; }

					td {
						border-bottom: unset;
					}
				}
			}
		}

		&.mobile-style:not(.force-header) {
			padding: 0;

			.load-more-wrapper {
				border: unset;
			}
		}
	}

    .data-table-wrapper {
        width: 100%;

		/*
		* Remove this if it breaks table layouts
		* Seems to have fixed an issue with the width behaviors of columns in Favorites tables
		* João Dias
		*/
		table-layout: fixed;

		:deep(.data-table-row) {
			&:last-of-type {
				td:first-of-type { border-bottom-left-radius: 12px; }
				td:last-of-type { border-bottom-right-radius: 12px; }
			}
		}
    }

	&.no-box-styles {
		width: 100%;

		/*
		* Remove this if it breaks table layouts
		* Seems to have fixed an issue with the width behaviors of columns in Favorites tables
		* João Dias
		*/
		table-layout: fixed;

        .data-table-wrapper {
            :deep(.table-header-cell) {
                border: unset;
            }

            :deep(.data-table-row) {
                td { border: unset; }
                td { border: unset; }
            }
        }
    }

	&.mobile-style {
		.data-table-wrapper {
			:deep(.data-table-row) {
				&:last-of-type {
					td:first-of-type { border-bottom-left-radius: 0; }
					td:last-of-type { border-bottom-right-radius: 0; }
				}
			}
		}
	}
}
</style>
